// components/Navbar.js
import {useState, useEffect} from "react";
import {motion, AnimatePresence} from "framer-motion";
import styles from './Navbar.module.css';
import { useLocation } from 'react-router-dom';

// --- Constants ---
const logoSrc = "/logo.png";
const navLinks = [
    {label: "Home", href: "/"},
    {label: "Menu", href: "/menu"},
    {label: "Online Order", href: "/order"},
    {label: "Checkout", href: "/checkout"},
    {label: "About US", href: "/contact"},
];
const utilityLinks = [
    {label: "Style Guide", href: "/template/style-guide"},
    {label: "Licensing", href: "/template/licensing"},
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [cartCount, setCartCount] = useState(0);
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [scrollDistance, setScrollDistance] = useState(0);
    const SCROLL_UP_THRESHOLD = 1000;

    // 计算购物车商品数量
    const updateCartCount = () => {
        try {
            const cart = JSON.parse(localStorage.getItem("cart")) || [];
            const count = cart.reduce((total, item) => total + (item.quantity || 0), 0);
            setCartCount(count);
        } catch (error) {
            console.error("Error calculating cart count:", error);
            setCartCount(0);
        }
    };

    // 滚动处理函数
    useEffect(() => {
        const handleScroll = () => {
            if (location.pathname === '/order') {
                const currentScrollY = window.scrollY;
                
                // 只有滚动到顶部时才显示navbar
                if (currentScrollY === 0) {
                    setIsVisible(true);
                } else {
                    const isScrollingDown = currentScrollY > lastScrollY;
                    
                    if (isScrollingDown) {
                        setIsVisible(false);
                    }
                    // 向上滚动时不主动显示navbar
                }

                setLastScrollY(currentScrollY);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [lastScrollY, location.pathname]);

    // 使用轮询方式定期检查购物车数量
    useEffect(() => {
        // 初始化购物车数量
        updateCartCount();

        // 设置定时器，每500毫秒检查一次
        const interval = setInterval(() => {
            updateCartCount();
        }, 500);

        // 清理定时器
        return () => {
            clearInterval(interval);
        };
    }, []);

    // Animation variants
    const overlayVariants = {
        hidden: {opacity: 0, y: "-10%"},
        visible: {opacity: 1, y: "0%"},
        exit: {opacity: 0, y: "-5%"},
    };

    const menuLinksVariants = {
        hidden: {opacity: 0, y: 60},
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: {delay: 0.1 + i * 0.08},
        }),
        exit: {opacity: 0, y: 60},
    };

    return (
        <header className={`${styles.navbar} ${isVisible ? styles.visible : styles.hidden}`}>
            {/* Main Navbar */}
            <div className={styles.mainNavbar}>
                {/* Logo */}
                <a href="/" className={styles.logoLink}>
                    <img
                        src={logoSrc}
                        alt="Asahi logo"
                        height={100}
                        className={styles.imageObjectContain}
                    />
                </a>

                {/* Right Side */}
                <div className={styles.cartContainer}>
                    {/* Cart */}
                    <a href="/checkout">
                        <div className={styles.cartText}>
                            <span>CART</span>
                            <span className={styles.cartCount}>
                          {cartCount}
                        </span>
                        </div>
                    </a>
                    {/* Hamburger */}
                    <button
                        aria-label="Open menu"
                        onClick={() => setOpen(true)}
                        className={styles.hamburgerButton}
                    >
                        <span className={styles.hamburgerLine}></span>
                        <span className={styles.hamburgerLine}></span>
                    </button>
                </div>
            </div>

            {/* Mobile / Overlay Menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="menu"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={overlayVariants}
                        transition={{duration: 0.38, ease: [0.25, 1, 0.5, 1]}}
                        className={styles.mobileMenu}
                    >
                        {/* Top Row: Logo and Close */}
                        <div className={styles.menuTop}>
                            <a href="/" className={styles.logoLink}>
                                <img
                                    src={logoSrc}
                                    alt="Asahi logo"
                                    width={120}
                                    height={36}
                                    className={styles.imageObjectContain}
                                />
                            </a>
                            <button
                                aria-label="Close menu"
                                onClick={() => setOpen(false)}
                                className={styles.closeButton}
                            >
                                <span className={styles.closeIcon}></span>
                                <span className={styles.closeIcon}></span>
                            </button>
                        </div>
                        {/* Menu Content */}
                        <div className={styles.menuContent}>
                            {/* Left: Links */}
                            <div className={styles.linksContainer}>
                                <nav className={styles.menuNav}>
                                    {navLinks.map((l, i) => (
                                        <motion.a
                                            key={l.label}
                                            href={l.href}
                                            variants={menuLinksVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                            custom={i}
                                            className={styles.menuLink}
                                            onClick={() => setOpen(false)}
                                        >
                                            {l.label.toUpperCase()}
                                        </motion.a>
                                    ))}
                                </nav>
                            </div>
                            {/* Right: Large Logo */}
                            <div className={styles.logoContainer}>
                                <motion.div
                                    initial={{opacity: 0, scale: 0.92}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.92}}
                                    transition={{delay: 0.2, duration: 0.6}}
                                >
                                    <img
                                        src={logoSrc}
                                        alt="Asahi logo big"
                                        width={200}
                                        height={70}
                                        className={styles.imageObjectContain}
                                    />
                                </motion.div>
                            </div>
                        </div>
                        {/* Bottom utility links */}
                        <div className={styles.utilityLinks}>
                            <div className={styles.utilityNav}>
                                {utilityLinks.map((l, i) => (
                                    <a key={l.label} href={l.href} className={styles.utilityLink}>{l.label}</a>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
